import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { getCreditBreakdown } from '../../services/creditService';
import { getUnreadMessageCount } from '../../services/messagingService';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const Sidebar = ({ userRole = 'client' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [creditBalance, setCreditBalance] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const msgChannelRef = useRef(null);
  const notifChannelRef = useRef(null);

  // Store collapsed state in localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState !== null) {
      setIsCollapsed(savedState === 'true');
    }
  }, []);

  // Listen for localStorage changes from other components (dashboard toggle buttons)
  useEffect(() => {
    const handleStorageChange = () => {
      const savedState = localStorage.getItem('sidebarCollapsed');
      if (savedState !== null) {
        setIsCollapsed(savedState === 'true');
      }
    };

    // Listen for custom storage events
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for same-window changes
    const interval = setInterval(() => {
      const savedState = localStorage.getItem('sidebarCollapsed');
      const currentState = isCollapsed;
      if (savedState !== null && (savedState === 'true') !== currentState) {
        setIsCollapsed(savedState === 'true');
      }
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [isCollapsed]);

  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
    
    // Update body class for content area adjustment
    if (newState) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
  };

  // Apply body class on mount based on saved state
  useEffect(() => {
    if (isCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    
    return () => {
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [isCollapsed]);

  const navigationItems = {
    client: [
      {
        label: 'Dashboard',
        path: '/client-dashboard',
        icon: 'LayoutDashboard',
      },
      {
        label: 'New Case',
        path: '/new-case-creation-wizard',
        icon: 'FilePlus',
      },
      {
        label: 'Activity',
        path: '/client-dashboard/activity',
        icon: 'Activity',
      },
      {
        label: 'Financial',
        path: '/client-dashboard/financial',
        icon: 'DollarSign',
      },
      {
        label: 'Messages',
        path: '/client-dashboard/messages',
        icon: 'MessageSquare',
      },
      {
        label: 'Marketplace',
        path: '/expert-marketplace-listing',
        icon: 'Store',
      },
    ],
    expert: [
      {
        label: 'Dashboard',
        path: '/expert-dashboard',
        icon: 'LayoutDashboard',
      },
      {
        label: 'Contracts',
        path: '/expert-dashboard/contracts',
        icon: 'FileText',
      },
      // {
      //   label: 'My Cases',
      //   path: '/expert-dashboard/cases',
      //   icon: 'Briefcase',
      // },
      // {
      //   label: 'Time Tracking',
      //   path: '/expert-dashboard/time-tracking',
      //   icon: 'Clock',
      // },
      {
        label: 'Messages',
        path: '/expert-dashboard/messages',
        icon: 'MessageSquare',
      },
      {
        label: 'Financial',
        path: '/expert-dashboard/financial',
        icon: 'DollarSign',
      },
      {
        label: 'Marketplace',
        path: '/expert-marketplace-listing',
        icon: 'Store',
      },
    ],
    admin: [
       {
        label: 'Overview',
        path: '/admin-overview',
        icon: 'LayoutDashboard',
      },
      {
        label: 'Business Management',
        path: '/admin-business',
        icon: 'Briefcase',
      },
      {
        label: 'All Cases',
        path: '/report-viewer',
        icon: 'FolderOpen',
      },
      {
        label: 'Marketplace',
        path: '/expert-marketplace-listing',
        icon: 'Store',
      },
      {
        label: 'Monitoring',
        path: '/admin-monitoring',
        icon: 'ShieldAlert',
      },
      {
        label: 'Users',
        path: '/admin-users',
        icon: 'Users',
      },
      {
        label: 'Contacts',
        path: '/admin-complaints',
        icon: 'MessageCircle',
      },
    ],
  };

  const currentNavItems = navigationItems?.[userRole] || navigationItems?.client;

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const isActive = (path) => {
    const fullPath = location?.pathname + location?.search;
    // Exact match (handles paths with query strings too)
    if (fullPath === path) return true;
    if (location?.pathname === path) return true;
    // For expert-dashboard main page, only match exact path
    if (path === '/expert-dashboard' && location?.pathname === '/expert-dashboard') return true;
    // For sub-paths, check if current path starts with the nav path
    if (path.includes('/expert-dashboard/') && location?.pathname.startsWith(path)) return true;
    // For client-dashboard sub-routes
    if (path === '/client-dashboard/activity' && location?.pathname === '/client-dashboard/activity') return true;
    if (path === '/client-dashboard/financial' && location?.pathname === '/client-dashboard/financial') return true;
    return false;
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileOpen]);

  useEffect(() => {
    if (user && userRole === 'client') {
      loadCreditBalance();
    }
  }, [user, userRole]);

  // ── Unread message count + live notification toasts ──
  useEffect(() => {
    if (!user?.id) return;

    // Initial fetch
    getUnreadMessageCount(user.id).then(r => {
      if (r?.success) setUnreadMessages(r.count);
    });

    // Realtime: new message → increment badge
    // Realtime: message updated (read_at set) → re-fetch true count
    msgChannelRef.current = supabase
      .channel(`sidebar-messages-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `receiver_id=eq.${user.id}`,
      }, () => {
        setUnreadMessages(prev => prev + 1);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'messages',
        filter: `receiver_id=eq.${user.id}`,
      }, () => {
        // Re-fetch the real count whenever a message is marked read
        getUnreadMessageCount(user.id).then(r => {
          if (r?.success) setUnreadMessages(r.count);
        });
      })
      .subscribe();

    // Realtime: new notification row → toast with title + message preview
    notifChannelRef.current = supabase
      .channel(`sidebar-notifications-${user.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'user_notifications',
        filter: `user_id=eq.${user.id}`,
      }, (payload) => {
        const n = payload.new;
        if (!n) return;
        const title = n.title || 'New notification';
        const body  = n.message ? String(n.message).slice(0, 80) : '';
        const toastContent = body ? `${title}\n${body}` : title;
        const opts = { duration: 5000 };
        const icon =
          n.type === 'new_message'       ? '💬' :
          n.type === 'contract_offer'    ? '📄' :
          n.type === 'contract_update'   ? '📋' :
          n.type === 'milestone_update'  ? '🏁' :
          n.type === 'time_entry_update' ? '⏱️' :
          n.type === 'warning'           ? '⚠️' : '🔔';
        if (n.type === 'success') toast.success(toastContent, opts);
        else if (n.type === 'error') toast.error(toastContent, opts);
        else toast(toastContent, { ...opts, icon });
      })
      .subscribe();

    return () => {
      if (msgChannelRef.current) supabase.removeChannel(msgChannelRef.current);
      if (notifChannelRef.current) supabase.removeChannel(notifChannelRef.current);
    };
  }, [user?.id]);

  const loadCreditBalance = async () => {
    try {
      const result = await getCreditBreakdown(user?.id);
      if (result?.success) {
        setCreditBalance(result?.breakdown?.total);
      }
    } catch (error) {
      console.error('Load credit balance error:', error);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <>
      <aside
        className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${
          isMobileOpen ? 'open' : ''
        }`}
      >
        <button 
          className="sidebar-header"
          onClick={() => navigate('/')}
          aria-label="Go to home page"
        >
          {isCollapsed ? (
            <div className="sidebar-logo">
              <img src="/assets/images/logo.png" alt="FireCaseIQ" className="w-7 h-7 object-contain" />
            </div>
          ) : (
            <img src="/assets/images/logo.png" alt="FireCaseIQ" className="h-8 object-contain" />
          )}
        </button>

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <nav className="sidebar-nav">
            {currentNavItems?.map((item) => (
              <div
                key={item?.path}
                className={`sidebar-nav-item ${
                  isActive(item?.path) ? 'active' : ''
                }`}
                onClick={() => {
                  handleNavigation(item?.path);
                }}
                role="button"
                tabIndex={0}
                title={isCollapsed ? item?.label : ''}
                onKeyDown={(e) => {
                  if (e?.key === 'Enter' || e?.key === ' ') {
                    handleNavigation(item?.path);
                  }
                }}
              >
                <div className="sidebar-nav-icon" style={{ position: 'relative' }}>
                  <Icon
                    name={item?.icon}
                    size={20}
                    color={isActive(item?.path) ? 'currentColor' : 'currentColor'}
                  />
                  {item?.label === 'Messages' && unreadMessages > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        right: '-6px',
                        minWidth: '16px',
                        height: '16px',
                        borderRadius: '9999px',
                        background: 'var(--color-destructive)',
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0 3px',
                        lineHeight: 1,
                      }}
                    >
                      {unreadMessages > 99 ? '99+' : unreadMessages}
                    </span>
                  )}
                </div>
                {!isCollapsed && (
                  <span className="sidebar-nav-label">
                    {item?.label}
                    {item?.label === 'Messages' && unreadMessages > 0 && (
                      <span className="ml-auto pl-2 text-xs font-bold text-destructive">
                        {unreadMessages > 99 ? '99+' : unreadMessages}
                      </span>
                    )}
                  </span>
                )}
              </div>
            ))}
          </nav>
        </div>

        <div className="border-t border-border p-4">
          {/* User Profile Section */}
          {user && (
            <div>
              <div 
                role="button"
                tabIndex={0}
                onClick={() => navigate('/account')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/account'); }}
                className={`flex items-center gap-3 mb-3 cursor-pointer hover:bg-muted rounded-lg p-1 -m-1 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
                title={isCollapsed ? (userProfile?.full_name || user?.email || 'Account Settings') : 'Account Settings'}
              >
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="User" size={20} className="text-primary" />
                </div>
                {!isCollapsed && (
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {userProfile?.full_name || 'User'}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {user?.email}
                    </p>
                  </div>
                )}
              </div>

              {/* Credit Balance — clients only */}
              {userRole === 'client' && !isCollapsed && (
                <div className="bg-primary/5 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="Zap" size={16} className="text-primary" />
                      <span className="text-xs text-muted-foreground">Credits</span>
                    </div>
                    <span className="text-lg font-bold text-primary">{creditBalance}</span>
                  </div>
                </div>
              )}
              {userRole === 'client' && isCollapsed && (
                <div 
                  className="flex justify-center mb-3"
                  title={`${creditBalance} Credits`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="Zap" size={16} className="text-primary" />
                  </div>
                </div>
              )}

              <button
                onClick={handleSignOut}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-destructive hover:bg-destructive/10 rounded-lg transition-smooth ${
                  isCollapsed ? 'justify-center' : ''
                }`}
                title={isCollapsed ? 'Sign Out' : ''}
              >
                <Icon name="LogOut" size={18} />
                {!isCollapsed && <span>Sign Out</span>}
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;